import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useDeliveryAddress } from '../context/DeliveryAddressContext';
import { API_URL } from '../config/api';
import { getFullImageUrl } from '../utils/imageUtils';
import { isCurrentlyOpen, getTodayOpeningHours } from '../utils/openingHours';
import { getRestaurantCategoryIcon } from '../utils/categoryIcons';
import SEO from '../components/SEO';
import './Home.css';

const CATEGORIES_VISIBLE_DEFAULT = 10;

const Restaurants = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language && i18n.language.startsWith('fi')) ? 'fi' : 'en';
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { currentAddress, addressVersion } = useDeliveryAddress();
  const [restaurants, setRestaurants] = useState([]);
  const [filteredRestaurants, setFilteredRestaurants] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Everything');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [showAllCategories, setShowAllCategories] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_URL}/restaurants/categories`);
        const list = res.data.categories || [];
        setCategories(Array.isArray(list) ? list.map((c) => {
          if (typeof c === 'string') return { value: c, fi: c, en: c };
          const v = c?.value ?? c?.fi ?? c?.en ?? '';
          return { value: v, fi: c?.fi ?? v, en: c?.en ?? v };
        }) : []);
      } catch (e) {
        console.error('Error fetching restaurant categories:', e);
      }
    };
    fetchCategories();
  }, []);

  const fetchRestaurants = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (selectedCategory !== 'Everything' && selectedCategory) {
        params.category = selectedCategory;
      }
      
      if (currentAddress?.coordinates?.lat != null && currentAddress?.coordinates?.lng != null) {
        const lat = parseFloat(currentAddress.coordinates.lat);
        const lng = parseFloat(currentAddress.coordinates.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          params.lat = lat;
          params.lng = lng;
        }
      }

      const response = await axios.get(`${API_URL}/restaurants`, { params });
      setRestaurants(response.data);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, currentAddress, addressVersion]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  useEffect(() => {
    filterRestaurants();
  }, [restaurants, selectedCategory, searchQuery]);

  const filterRestaurants = () => {
    let filtered = restaurants;

    if (searchQuery) {
      filtered = filtered.filter((restaurant) =>
        restaurant.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredRestaurants(filtered);
  };

  const handleCategoryChange = async (category) => {
    setSelectedCategory(category);
  };

  const handleAddToCart = (restaurant, item) => {
    addToCart(restaurant._id, restaurant.name, item);
  };

  const toggleFavorite = async (restaurantId) => {
    if (!user) return;
    
    try {
      const isFavorite = user.favoriteRestaurants?.includes(restaurantId);
      const endpoint = `${API_URL}/users/favorites/${restaurantId}`;
      const method = isFavorite ? 'delete' : 'post';
      await axios[method](endpoint);
      
      const updatedFavorites = isFavorite
        ? user.favoriteRestaurants.filter(id => id !== restaurantId)
        : [...(user.favoriteRestaurants || []), restaurantId];
      
      if (window.location) {
        setTimeout(() => window.location.reload(), 100);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const isFavorite = (restaurantId) => {
    return user?.favoriteRestaurants?.includes(restaurantId) || false;
  };

  const isRestaurantOpen = (restaurant) => isCurrentlyOpen(restaurant);

  const renderStars = (rating) => {
    if (!rating) return null;
    const stars = [];
    const roundedRating = Math.round(rating);
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i key={i} className={i <= roundedRating ? 'fas fa-star' : 'far fa-star'}></i>
      );
    }
    return <span className="rating-stars">{stars}</span>;
  };

  return (
    <div className="home-container">
      <SEO
        title="Ravintolat"
        description="Selaa ravintoloita ja tilaa ruoka kotiin. Pizzat, burgerit, aasialainen ja lisää – valitse ravintola ja tilaa helposti."
        path="/restaurants"
      />
      <div className="container">
        <div className="categories categories-with-icons">
          <button
            key="Everything"
            type="button"
            className={`category-btn category-btn-with-icon ${selectedCategory === 'Everything' ? 'active' : ''}`}
            onClick={() => handleCategoryChange('Everything')}
          >
            <i className={getRestaurantCategoryIcon('Everything')} aria-hidden="true" />
            <span>{t('home.categories.everything')}</span>
          </button>
          {(showAllCategories ? categories : categories.slice(0, CATEGORIES_VISIBLE_DEFAULT)).map((cat) => {
            const value = (cat && (cat.value ?? cat.fi ?? cat.en)) || '';
            const displayLabel = (cat && (cat[lang] || cat.fi || cat.en)) || value;
            return (
              <button
                key={value}
                type="button"
                className={`category-btn category-btn-with-icon ${selectedCategory === value ? 'active' : ''}`}
                onClick={() => handleCategoryChange(value)}
              >
                <i className={getRestaurantCategoryIcon(value)} aria-hidden="true" />
                <span>{displayLabel}</span>
              </button>
            );
          })}
          {categories.length > CATEGORIES_VISIBLE_DEFAULT && (
            <button
              type="button"
              className="category-btn category-btn-more"
              onClick={() => setShowAllCategories(!showAllCategories)}
              aria-expanded={showAllCategories}
              aria-label={showAllCategories ? (t('home.showLessCategories') || 'Show less categories') : (t('home.showMoreCategories') || 'Show more categories')}
            >
              <i className={showAllCategories ? 'fas fa-chevron-up' : 'fas fa-chevron-down'} aria-hidden="true" />
              <span>{showAllCategories ? (t('home.showLess') || 'Less') : (t('home.showMore') || 'More')}</span>
            </button>
          )}
        </div>

        <div className="search-section">
          <input
            type="text"
            placeholder={t('home.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        {loading ? (
          <div>Loading restaurants...</div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="no-restaurants">{t('home.noRestaurants')}</div>
        ) : (
          <div className="restaurants-grid">
            {filteredRestaurants.map((restaurant) => (
              <Link key={restaurant._id} to={`/restaurant/${restaurant._id}`} className="restaurant-card">
                <div className="restaurant-header">
                  {restaurant.image && (
                    <div className="restaurant-image-container">
                      <img src={getFullImageUrl(restaurant.image)} alt={restaurant.name} className="restaurant-image" loading="lazy" decoding="async" />
                      {restaurant.openingHours && !isRestaurantOpen(restaurant) && (
                        <div className="restaurant-status-overlay">
                          {t('restaurant.closed')}
                        </div>
                      )}
                      {currentAddress?.coordinates?.lat &&
                       currentAddress?.coordinates?.lng &&
                       restaurant.distance !== undefined &&
                       restaurant.distance !== null &&
                       !isNaN(restaurant.distance) && (() => {
                        const maxDist = restaurant.maxDeliveryDistance ?? 20;
                        if (restaurant.distance > maxDist) {
                          return (
                            <div className="restaurant-delivery-overlay">
                              <span>{t('restaurant.outsideDeliveryArea')}</span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}
                  {user && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleFavorite(restaurant._id);
                      }}
                      className={`favorite-btn ${isFavorite(restaurant._id) ? 'active' : ''}`}
                    >
                      <i className={`fas fa-heart ${isFavorite(restaurant._id) ? 'active' : ''}`}></i>
                    </button>
                  )}
                </div>
                <div className="restaurant-info">
                  <div className="restaurant-title-row">
                    <h3>{restaurant.name}</h3>
                    {(restaurant.rating != null && restaurant.rating > 0) ? renderStars(restaurant.rating) : null}
                  </div>
                  <p className="restaurant-description">{restaurant.description}</p>
                  <div className="pricing-range">{restaurant.pricingRange || '€€'}</div>
                  <div className="restaurant-details">
                    <span>{t('restaurant.deliveryTime')}: {restaurant.deliveryTime || '30-45 min'}</span>
                    <span>{t('restaurant.deliveryFee')}: €{restaurant.deliveryFee || '0'}</span>
                    {restaurant.distance && (
                      <span>{restaurant.distance} {t('restaurant.distance') || 'km'}</span>
                    )}
                    {(() => {
                      const today = getTodayOpeningHours(restaurant);
                      if (today) return <span>{today.openingTime} – {today.closingTime}</span>;
                      if (restaurant?.openingHours) return <span>{t('restaurant.closedToday') || 'Closed today'}</span>;
                      return null;
                    })()}
                  </div>
                  {restaurant.deliveryMethods && (
                    <div className="delivery-methods">
                      {restaurant.deliveryMethods.delivery && (
                        <span className="delivery-badge">{t('restaurant.delivery')}</span>
                      )}
                      {restaurant.deliveryMethods.pickup && (
                        <span className="delivery-badge">{t('restaurant.pickup')}</span>
                      )}
                      {restaurant.deliveryMethods.eatIn && (
                        <span className="delivery-badge">{t('restaurant.eatIn')}</span>
                      )}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Restaurants;
